import { useCallback, useEffect, useState } from "react";
import * as Api from "../../api";
import * as OfficeHelpers from "../../officeHelpers";
import { MessageBarType } from "@fluentui/react/lib/MessageBar";

const signInUrl =
  location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + "/auth.html";

async function signInSilently() {
  const identityToken = await OfficeHelpers.getUserIdentityToken();
  const token = await Api.getToken(identityToken);
  if (token) {
    Api.setAuthToken(token);
    return await Api.authenticate();
  }
}

async function signIn() {
  return new Promise((resolve, reject) => {
    OfficeHelpers.displayDialog(signInUrl, { height: 60, width: 40 }).then((dialog) => {
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if (arg) {
          const token = JSON.parse(arg.message);
          resolve(token);
        }
        dialog.close();
      });
      // No need to close dialog when receiving these events
      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
        switch (arg.error) {
          case 12006:
            // User closed dialog
            resolve(null);
            break;
          default:
            // Should never get here under normal circumstances
            // See https://docs.microsoft.com/en-us/office/dev/add-ins/reference/javascript-api-for-office-error-codes for error codes
            reject(Error(`Failed to open sign in page (Error ${arg.error})`));
        }
      });
    });
  });
}

export default function useAuth(setMessage) {
  const [signingIn, setSigningIn] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [auth, setAuth] = useState(null);

  const onSignIn = useCallback(async (silently = false) => {
    try {
      setSigningIn(true);
      let auth;
      try {
        auth = await signInSilently();
        if (auth) {
          setAuth(auth);
          return;
        }
        if (silently) {
          return;
        }
      } catch (e) {
        if (silently) {
          throw e;
        }
      }
      const token = await signIn();
      if (token) {
        const identityToken = await OfficeHelpers.getUserIdentityToken();
        Api.setAuthToken(token);
        Api.postToken(identityToken, token);
        auth = await Api.authenticate();
      }
      setAuth(auth);
    } catch (e) {
      console.error(e);
      setMessage({ text: "Failed to sign in. Please try again.", type: MessageBarType.error });
    } finally {
      setSigningIn(false);
    }
  }, []);

  const onSignOut = useCallback(async () => {
    try {
      setSigningOut(true);
      await Api.unauthenticate();
      const identityToken = await OfficeHelpers.getUserIdentityToken();
      Api.deleteToken(identityToken);
      setAuth(null);
    } catch (e) {
      console.error(e);
      setMessage({ text: "Failed to sign out. Please try again.", type: MessageBarType.error });
      return;
    } finally {
      setSigningOut(false);
    }
  });

  useEffect(() => {
    // Sign in silently when mounted
    onSignIn(true);
  }, []);

  useEffect(() => {
    if (signingIn || signingOut) {
      setMessage(null);
    }
  }, [signingIn, signingOut]);

  return [signingIn, signingOut, auth, onSignIn, onSignOut];
}
